import { TargetFunction, TargetType } from '../store/reducers/workspace.reducer.ts';

/**
 * Returns the correct mathematical symbol for photometric measurements
 * based on the function type and target type.
 */
export function getPhotometricSymbol(func: TargetFunction, type: TargetType): JSX.Element | string {
  // Handle uniformity ratios - special formatting
  if (func === 'Uo') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            Ū<sub>0</sub>
          </>
        );
      case 'MIN':
        return (
          <>
            U<sub>0,min</sub>
          </>
        );
      case 'MAX':
        return (
          <>
            U<sub>0,max</sub>
          </>
        );
      default:
        return (
          <>
            U<sub>0</sub>
          </>
        );
    }
  }
  if (func === 'Ul') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            Ū<sub>l</sub>
          </>
        );
      case 'MIN':
        return (
          <>
            U<sub>l,min</sub>
          </>
        );
      case 'MAX':
        return (
          <>
            U<sub>l,max</sub>
          </>
        );
      default:
        return (
          <>
            U<sub>l</sub>
          </>
        );
    }
  }
  if (func === 'Uow') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            Ū<sub>0</sub>w
          </>
        );
      case 'MIN':
        return (
          <>
            U<sub>0</sub>w<sub>,min</sub>
          </>
        );
      case 'MAX':
        return (
          <>
            U<sub>0</sub>w<sub>,max</sub>
          </>
        );
      default:
        return (
          <>
            U<sub>0</sub>w
          </>
        );
    }
  }

  // Handle REI (Edge Illuminance Ratio)
  if (func === 'REI') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            R̄<sub>EI</sub>
          </>
        );
      case 'MIN':
        return (
          <>
            R<sub>EI,min</sub>
          </>
        );
      case 'MAX':
        return (
          <>
            R<sub>EI,max</sub>
          </>
        );
      default:
        return (
          <>
            R<sub>EI</sub>
          </>
        );
    }
  }

  // Handle fTI (Threshold Increment)
  if (func === 'fTI') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            f̄<sub>TI</sub>
          </>
        );
      case 'MIN':
        return (
          <>
            f<sub>TI,min</sub>
          </>
        );
      case 'MAX':
        return (
          <>
            f<sub>TI,max</sub>
          </>
        );
      default:
        return (
          <>
            f<sub>TI</sub>
          </>
        );
    }
  }

  // Handle Ev (vertical illuminance)
  if (func === 'Ev') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            Ē<sub>v</sub>
          </>
        );
      case 'MIN':
        return (
          <>
            E<sub>v,min</sub>
          </>
        );
      case 'MAX':
        return (
          <>
            E<sub>v,max</sub>
          </>
        );
      default:
        return (
          <>
            E<sub>v</sub>
          </>
        );
    }
  }

  // Handle Esc (semi-cylindrical illuminance)
  if (func === 'Esc') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            Ē<sub>sc</sub>
          </>
        );
      case 'MIN':
        return (
          <>
            E<sub>sc,min</sub>
          </>
        );
      case 'MAX':
        return (
          <>
            E<sub>sc,max</sub>
          </>
        );
      default:
        return (
          <>
            E<sub>sc</sub>
          </>
        );
    }
  }

  // Handle Ehs (horizontal illuminance on surface)
  if (func === 'Ehs') {
    switch (type) {
      case 'AVERAGE':
        return (
          <>
            Ē<sub>hs</sub>
          </>
        );
      case 'MIN':
        return (
          <>
            E<sub>hs,min</sub>
          </>
        );
      case 'MAX':
        return (
          <>
            E<sub>hs,max</sub>
          </>
        );
      default:
        return (
          <>
            E<sub>hs</sub>
          </>
        );
    }
  }

  // Handle E (illuminance)
  if (func === 'E') {
    switch (type) {
      case 'AVERAGE':
        return <>Ē</>;
      case 'MIN':
        return (
          <>
            E<sub>min</sub>
          </>
        );
      case 'MAX':
        return (
          <>
            E<sub>max</sub>
          </>
        );
      default:
        return <>E</>;
    }
  }

  // Handle L (luminance)
  if (func === 'L') {
    switch (type) {
      case 'AVERAGE':
        return <>L̄</>;
      case 'MIN':
        return (
          <>
            L<sub>min</sub>
          </>
        );
      case 'MAX':
        return (
          <>
            L<sub>max</sub>
          </>
        );
      default:
        return <>L</>;
    }
  }

  // Fallback for any other function
  return func;
}
